import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../features/auth/AuthContext";
import { AuthLayout } from "../features/auth/AuthLayout";
import { useForm } from "../hooks/useForm";
import { Input } from "../components/Input";
import { Button } from "../components/Button";
import { Alert } from "../components/Alert";
import { cn } from "../lib/utils";
import { IS_GRIP_SITE } from "../lib/site";
import { OAuthButtons } from "../features/auth/OAuthButtons";
import { peekPostAuthRedirect } from "../lib/postAuthRedirect";
import { AuthShowcase } from "../features/auth/AuthShowcase";
import { gateObject, gateTarget, useGateRace } from "../features/auth/gate";
import { useState } from "react";

function PasswordStrength({ password }: { password: string }) {
  const checks = [
    { label: "8+ characters", met: password.length >= 8 },
    { label: "Lowercase letter", met: /[a-z]/.test(password) },
    { label: "Uppercase letter", met: /[A-Z]/.test(password) },
    { label: "Number", met: /[0-9]/.test(password) },
  ];

  if (!password) return null;

  const metCount = checks.filter((c) => c.met).length;

  return (
    <div className="space-y-2 mt-2">
      <div className="flex gap-1.5">
        {checks.map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-1.5 flex-1 rounded-full transition-colors",
              i < metCount
                ? metCount <= 2
                  ? "bg-red-400"
                  : metCount === 3
                    ? "bg-yellow-400"
                    : "bg-green-400"
                : "bg-gray-200 dark:bg-gray-700"
            )}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
        {checks.map((check) => (
          <p
            key={check.label}
            className={cn(
              "text-xs",
              check.met
                ? "text-green-600 dark:text-green-400"
                : "text-gray-400 dark:text-gray-500"
            )}
          >
            {check.met ? "✓" : "○"} {check.label}
          </p>
        ))}
      </div>
    </div>
  );
}

const SIGNUP_SUBTITLE = IS_GRIP_SITE
  ? "Free tire pressure and damper tools"
  : "Every lap of every race, free.";

export function SignUpPage() {
  const { register, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  // What the visitor's link opens (they were sent here from a race link): named in the title.
  const heading = peekPostAuthRedirect();
  const forGrip = IS_GRIP_SITE || !!heading?.startsWith("/grip");
  const target = forGrip ? null : gateTarget(heading);
  const gateRace = useGateRace(target);
  const opens = gateObject(target, gateRace);
  const title = forGrip ? "Create your account" : opens ? `Sign up free to open ${opens}` : "Create your free account";
  const aside = forGrip ? undefined : <AuthShowcase target={target} race={gateRace} free={false} />;

  const { values, errors, globalError, isSubmitting, handleChange, handleSubmit } =
    useForm({
      initialValues: { displayName: "", email: "", password: "" },
      validate: (vals) => {
        const errs: Record<string, string> = {};
        if (!vals.email) errs.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(vals.email))
          errs.email = "Invalid email address";
        if (!vals.password) errs.password = "Password is required";
        else if (vals.password.length < 8)
          errs.password = "Password must be at least 8 characters";
        else if (!/[a-z]/.test(vals.password))
          errs.password = "Must contain a lowercase letter";
        else if (!/[A-Z]/.test(vals.password))
          errs.password = "Must contain an uppercase letter";
        else if (!/[0-9]/.test(vals.password))
          errs.password = "Must contain a number";
        return errs;
      },
      onSubmit: async (vals) => {
        await register(vals.email, vals.password, vals.displayName || undefined);
        // RaceTrace has a welcome tour; Finding Grip goes straight to the app.
        navigate(IS_GRIP_SITE ? "/dashboard" : "/onboarding", { replace: true });
      },
    });

  if (isLoading) {
    return (
      <AuthLayout title={title} subtitle={SIGNUP_SUBTITLE}>
        <div className="flex justify-center py-12">
          <div className="h-8 w-8 border-4 border-brand-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </AuthLayout>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthLayout title={title} subtitle={SIGNUP_SUBTITLE} aside={aside}>
      <OAuthButtons next={peekPostAuthRedirect() || "/dashboard"} verb="Sign up" />
      <form onSubmit={handleSubmit} className="space-y-5">
        {globalError && <Alert variant="error">{globalError}</Alert>}

        <Input
          label="Name"
          name="displayName"
          type="text"
          autoComplete="name"
          placeholder="Your name (optional)"
          value={values.displayName}
          onChange={handleChange}
          error={errors.displayName}
        />

        <Input
          label="Email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          value={values.email}
          onChange={handleChange}
          error={errors.email}
        />

        <div className="relative">
          {/* One password field with a Show switch, in place of typing it twice. */}
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-0 top-0 text-xs font-medium text-brand-600 hover:underline dark:text-brand-400"
            aria-controls="password"
            aria-pressed={showPassword}
          >
            {showPassword ? "Hide" : "Show"}
          </button>
          <Input
            label="Password"
            name="password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder="••••••••"
            value={values.password}
            onChange={handleChange}
            error={errors.password}
          />
          <PasswordStrength password={values.password} />
        </div>

        <Button type="submit" loading={isSubmitting} className="w-full" size="lg">
          {forGrip ? "Create account" : "Create free account"}
        </Button>
        {!forGrip && <p className="-mt-2 text-center text-xs text-gray-500">Free for everyone: no credit card, no ads.</p>}
      </form>

      <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        Already have an account?{" "}
        <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline font-medium">
          Log in
        </Link>
      </p>
    </AuthLayout>
  );
}
